import { ref, computed, onMounted, onUnmounted } from 'vue'
import { BREAKPOINTS } from '@/constants/breakpoints'

/**
 * Composable for handling responsive behavior
 * Provides reactive screen size detection and breakpoint checks
 */
export const useResponsive = () => {
  // Reactive state
  const windowWidth = ref(window.innerWidth)
  const windowHeight = ref(window.innerHeight)

  // Update dimensions on resize
  const handleResize = () => {
    windowWidth.value = window.innerWidth
    windowHeight.value = window.innerHeight
  }

  // Computed breakpoint checks
  const isMobile = computed(() => {
    return windowWidth.value <= BREAKPOINTS.MOBILE_MAX
  })

  const isDesktop = computed(() => {
    return windowWidth.value >= BREAKPOINTS.DESKTOP_MIN
  })

  const isSmallScreen = computed(() => {
    return windowWidth.value < BREAKPOINTS.SMALL_SCREEN
  })

  const isLargeScreen = computed(() => {
    return windowWidth.value >= BREAKPOINTS.LARGE_SCREEN
  })

  // xlg (extra-large) breakpoint: min-width 769px AND min-height 1400px
  const isXLg = computed(() => {
    return windowWidth.value >= BREAKPOINTS.DESKTOP_MIN &&
           windowHeight.value >= BREAKPOINTS.XLG_HEIGHT
  })

  // lg breakpoint: min-width 769px AND max-height 1399px
  const isLg = computed(() => {
    return windowWidth.value >= BREAKPOINTS.DESKTOP_MIN &&
           windowHeight.value < BREAKPOINTS.XLG_HEIGHT
  })

  // Mobile device detection (checks user agent)
  const isMobileDevice = computed(() => {
    const userAgent = navigator.userAgent || window.opera
    return /iPhone|iPad|iPod|Android/i.test(userAgent)
  })

  // Lifecycle hooks
  onMounted(() => {
    window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
  })

  return {
    // Raw values
    windowWidth,
    windowHeight,

    // Computed breakpoints
    isMobile,
    isDesktop,
    isSmallScreen,
    isLargeScreen,
    isLg,
    isXLg,
    isMobileDevice
  }
}
